import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, Users, Clock, Activity, CheckCircle, Ambulance } from 'lucide-react';

const LoaderContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 8vh;
  z-index: 2000;
  gap: 2rem;
  min-height: 100vh;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 2rem;
  box-sizing: border-box;
`;

const LoaderContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 1.5rem;
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  max-width: 400px;
`;

const MainSpinner = styled(motion.div)`
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OuterRing = styled(motion.div)`
  position: absolute;
  width: 80px;
  height: 80px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
`;

const InnerRing = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  border: 2px solid #f1f5f9;
  border-bottom: 2px solid #1e40af;
  border-radius: 50%;
`;

const CenterIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  border-radius: 50%;
  color: white;
  z-index: 1;
`;

const LoadingText = styled(motion.div)`
  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.4;
  }
`;

const StatusDots = styled(motion.div)`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Dot = styled(motion.div)`
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
`;

const ProgressSteps = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 300px;
`;

const StepItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${props => props.active ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : '#f8fafc'};
  border-radius: 0.5rem;
  border: 1px solid ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  transition: all 0.3s ease;

  .step-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.completed ? '#10b981' : props.active ? '#3b82f6' : '#e2e8f0'};
    color: ${props => props.completed || props.active ? 'white' : '#64748b'};
    font-size: 12px;
  }

  .step-text {
    flex: 1;
    font-size: 0.875rem;
    color: ${props => props.active ? '#1e40af' : '#64748b'};
    font-weight: ${props => props.active ? '500' : '400'};
  }
`;

const emergencySteps = [
  { id: 1, text: 'Processing emergency request', icon: Activity },
  { id: 2, text: 'Finding available ambulances', icon: Ambulance },
  { id: 3, text: 'Connecting with medical teams', icon: Users },
  { id: 4, text: 'Confirming response details', icon: CheckCircle }
];

const homeCareSteps = [
  { id: 1, text: 'Creating home care request', icon: Activity },
  { id: 2, text: 'Finding available home care providers', icon: Users },
  { id: 3, text: 'Connecting with healthcare professionals', icon: CheckCircle },
  { id: 4, text: 'Confirming care details', icon: Clock }
];

export const ProfessionalLoader = ({
  isVisible = true,
  title = "Processing Request",
  subtitle = "Please wait while we process your request...",
  showSteps = true,
  currentStep = 1,
  isHomeCare = false
}) => {
  const steps = isHomeCare ? homeCareSteps : emergencySteps;
  return (
    <AnimatePresence>
      {isVisible && (
        <LoaderContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <LoaderContent
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 20 }}
            transition={{ duration: 0.4, type: "spring", damping: 25 }}
          >
            {/* Main Spinner */}
            <MainSpinner>
              <OuterRing
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              />
              <InnerRing
                animate={{ rotate: -360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              />
              <CenterIcon
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Ambulance size={20} />
              </CenterIcon>
            </MainSpinner>

            {/* Loading Text */}
            <LoadingText
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3>{title}</h3>
              <p>{subtitle}</p>
            </LoadingText>

            {/* Animated Dots */}
            <StatusDots>
              {[0, 1, 2].map((index) => (
                <Dot
                  key={index}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: index * 0.2
                  }}
                />
              ))}
            </StatusDots>

            {/* Progress Steps */}
            {showSteps && (
              <ProgressSteps
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;
                  
                  return (
                    <StepItem
                      key={step.id}
                      active={isActive}
                      completed={isCompleted}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                    >
                      <div className="step-icon">
                        {isCompleted ? (
                          <CheckCircle size={14} />
                        ) : (
                          <Icon size={14} />
                        )}
                      </div>
                      <div className="step-text">{step.text}</div>
                      {isActive && (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <Loader size={16} />
                        </motion.div>
                      )}
                    </StepItem>
                  );
                })}
              </ProgressSteps>
            )}
          </LoaderContent>
        </LoaderContainer>
      )}
    </AnimatePresence>
  );
};

export default ProfessionalLoader;
